import type {ReactNode} from 'react'
import React from 'react'

import type {CopyExperimentsContextType} from '@helios/shared/types/copy-experiment'
import {getExperiments} from '@helios/shared/utils/get-experiments'

// Context which is used to expose copy experiments to all the components. Copy experiments
// are stored in Prismic, fetched and then injected as value of this context
const CopyExperimentsContext = React.createContext<CopyExperimentsContextType[]>([])

type CopyExperimentsProviderProps = {content: CopyExperimentsContextType[]; children: ReactNode}
export const CopyExperimentsProvider = ({children, content}: CopyExperimentsProviderProps) => (
    <CopyExperimentsContext.Provider value={content}>{children}</CopyExperimentsContext.Provider>
)

// Extracts the copy experiments from the context and returns them in a convenient format
// {[experimentId]: ExperimentBucket[]}
export const useCopyExperiments = () => {
    const experiments = React.useContext(CopyExperimentsContext)
    return experiments.reduce((all, current) => {
        all[current.uid] = current.data.body.map((slice) => slice.primary)
        return all
    }, {} as Record<string, CopyExperimentsContextType['data']['body'][0]['primary'][]>)
}

export function useContentVariant<T>(content: T, id?: string): {content: T; isResolved: boolean} {
    const [isMounted, setIsMounted] = React.useState(false)
    React.useEffect(() => {
        setIsMounted(true)
    }, [])

    const copyExperiments = useCopyExperiments()
    const cookieExperiments = getExperiments()
    const buckets = id && copyExperiments[id]

    // if the text element doesn't have an id specified,
    // or there is no copy experiment configured for this copy id,
    // it cannot have active copy experiments, and we serve
    // the non-experimental version of the copy
    if (!id || !buckets) {
        return {content, isResolved: true}
    }

    const activeBucketId = cookieExperiments[id]

    // if the experiment is not specified in the experiments cookie
    // or the user is in the control bucket for this experiment
    // then we serve the non-experimental version of the copy
    // Note: if this is the SSG render pass, we will also hit this code path,
    // since there won't be any experiment cookies. In that case we return the
    // non-experimental version as well, but we'll annotate isResolved
    // to false, so that the copy can be hidden before the right variant
    // is resolved on the client.
    if (!activeBucketId || activeBucketId === 'control') {
        return {content, isResolved: isMounted}
    }

    const activeBucket = buckets.find((b) => b.id === activeBucketId)

    // if the bucket specified in the experiment cookie is not configured
    // in the copy experiments, the whole thing is misconfigured. Fall back
    // to the non-experimental version of the copy
    // TODO: logging an error to an error tracker once we have it
    if (!activeBucket) {
        return {content, isResolved: true}
    }

    // if we are here it means we have a copy experiment for the current text element
    // and we have the adjusted copy for the active bucket. We can return the modified copy,
    // for the format that matches the non-experimental version
    if (typeof content === 'string' && activeBucket.text) {
        return {content: activeBucket.text as unknown as T, isResolved: true}
    }
    if (typeof content !== 'string' && activeBucket.rich_text) {
        return {content: activeBucket.rich_text, isResolved: true}
    }

    // if non matching format is found, we fall back to the non-experimental version
    // of the copy
    // TODO: logging an error to an error tracker once we have it
    return {content, isResolved: true}
}
